import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Alert, Modal } from 'react-bootstrap';
import { getDataSourceNumericId } from '../DataSources/datasourcesApi';
import SourcePicker, { RULES } from 'DataSources/SourcePicker';
import { startProfileJob } from './profileApi';
import { ensureConnectionTested } from 'Utils/connectionGate';
import { useCanEdit, READ_ONLY_TITLE } from 'Auth/permissions';

/**
 * Pick one data source and start a profile (schema/file metadata) run on it, through the shared
 * SourcePicker. Both relational and file-folder sources are eligible — the metadata-service
 * profile worker accepts the `file` modality and returns a whole-folder inventory (total size +
 * per-file listing) for file sources. Rows come from AppContext.connections; a source that cannot
 * start a job (no credentials, platform-managed) is left out with its reason.
 */
const RunProfileModal = ({ show, onHide, onSuccess }) => {
  const history = useHistory();
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const canEdit = useCanEdit();

  useEffect(() => {
    if (show) setSubmitError(null);
  }, [show]);

  const handleRun = async (source) => {
    const id = getDataSourceNumericId(source);
    if (id == null) return;
    if (!canEdit) {
      setSubmitError(READ_ONLY_TITLE);
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Verify (or establish) a successful connection test first; on failure the
      // helper routes the user to the source editor to fix the connection.
      const gate = await ensureConnectionTested(source, { history });
      if (!gate.ok) {
        setSubmitError(`Connection test failed: ${gate.message || 'the data source is unreachable.'}`);
        onHide();
        return;
      }
      const job = await startProfileJob(id);
      if (onSuccess) onSuccess(job);
      onHide();
    } catch (e) {
      setSubmitError(e?.message || 'Failed to start fingerprint run');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size='xl' className='sp-modal'>
      <Modal.Header closeButton>
        <Modal.Title>Run fingerprint</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {submitError && <Alert variant='danger' className='m-3'>{submitError}</Alert>}
        {show && (
          <SourcePicker
            selection='single'
            restrict={[RULES.jobStartable]}
            verb='Run fingerprint'
            busy={submitting ? 'Starting…' : false}
            maxHeight={420}
            onConfirm={(picked) => handleRun(picked[0])}
            onCancel={onHide}
            testId='run-profile'
          />
        )}
      </Modal.Body>
    </Modal>
  );
};

export default RunProfileModal;
